import { Box } from "@mui/material";
import React, { useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import EdgeDetailOverlay from "../components/Layout/EdgeDetailOverlay";
import EntityDetail from "../components/Layout/EntityDetail";
import OverlayWrapper from "../components/Layout/OverlayWrapper";
import Sidebar from "../components/Layout/Sidebar";
import SearchView from "../components/Search/SearchView";

const ListView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Get entity ID from URL params
  const entityId = searchParams.get("detail_entity_id");

  // Handler to close entity detail overlay
  const handleCloseEntityDetail = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("detail_entity_id");
    setSearchParams(params, { replace: false });
  };

  // Handler to close relationship detail overlay
  const handleCloseRelationshipDetail = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("detail_relationship_id");
    setSearchParams(params, { replace: false });
  };

  // Handler for entity navigation within overlay
  const handleEntityNavigation = (entityId: string) => {
    const params = new URLSearchParams(searchParams);
    params.delete("detail_relationship_id"); // Clear relationship overlay
    params.set("detail_entity_id", entityId);
    setSearchParams(params, { replace: false });
  };

  // Handle clicking anywhere on the page to close overlay (except on interactive elements)
  const handlePageClick = (event: React.MouseEvent) => {
    // Don't close overlay if clicking on sidebar or other interactive elements
    const target = event.target as HTMLElement;

    // Check if the click is on the sidebar or its children
    const sidebar = target.closest("[data-sidebar]");
    if (sidebar) {
      return; // Let sidebar navigation proceed without closing overlay
    }

    // Check if clicking on overlay content itself
    const overlayContent = target.closest("[data-overlay-content]");
    if (overlayContent) {
      return; // Don't close when clicking inside overlay
    }

    // Check if clicking on MUI portaled components (menus, popovers, dialogs, etc.)
    const isMuiPortal = target.closest(
      ".MuiPopover-root, .MuiMenu-root, .MuiModal-root, .MuiDialog-root, .MuiTooltip-popper, .MuiAutocomplete-popper"
    );
    if (isMuiPortal) {
      return; // Don't close when clicking on MUI portaled components
    }

    // Only close if there's an overlay open and click is outside interactive elements
    if (
      searchParams.get("detail_entity_id") ||
      searchParams.get("detail_relationship_id")
    ) {
      const params = new URLSearchParams(searchParams);
      params.delete("detail_entity_id");
      params.delete("detail_relationship_id");
      setSearchParams(params, { replace: false });
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        backgroundColor: "background.default",
      }}
      onClick={handlePageClick}
    >
      {/* Sidebar */}
      <Sidebar />

      {/* Main Search Content */}
      <Box sx={{ flex: 1, position: "relative" }}>
        <SearchView />

        {/* Entity Detail Overlay */}
        {entityId && (
          <OverlayWrapper>
            <EntityDetail
              entityId={entityId}
              onClose={handleCloseEntityDetail}
              onEntityClick={handleEntityNavigation}
              mode="overlay"
              scrollContainerRef={scrollContainerRef}
            />
          </OverlayWrapper>
        )}

        {/* Edge Detail Overlay */}
        {searchParams.get("detail_relationship_id") && (
          <EdgeDetailOverlay
            relationshipId={searchParams.get("detail_relationship_id")!}
            onClose={handleCloseRelationshipDetail}
            onEntityClick={handleEntityNavigation}
          />
        )}
      </Box>
    </Box>
  );
};

export default ListView;
